import { useState, useCallback } from 'react';
import { useApi, ApiConfig } from './useApi';
import { Address, PaginatedResponse } from './types';

export interface UseAddressesOptions {
  poolId: number;
}

export function useAddresses(
  config: ApiConfig = {},
  options: UseAddressesOptions,
) {
  const { poolId } = options;
  const api = useApi(config);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [previousPage, setPreviousPage] = useState<string | null>(null);

  const fetchAddresses = useCallback(
    async (page = 1, pageSize = 50) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        const response = await api.get<PaginatedResponse<Address>>(
          `/ip/pools/${poolId}/addresses/?${params}`,
        );
        setAddresses(response.results);
        setTotalCount(response.count);
        setNextPage(response.next);
        setPreviousPage(response.previous);
        return response;
      } catch (error) {
        console.error('Failed to fetch addresses:', error);
        throw error;
      }
    },
    [api, poolId],
  );

  const getAddress = useCallback(
    async (addressId: number) => {
      return api.get<Address>(`/ip/pools/${poolId}/addresses/${addressId}/`);
    },
    [api, poolId],
  );

  const createAddress = useCallback(
    async (data: Omit<Address, 'id' | 'created_at'>) => {
      const response = await api.post<Address>(
        `/ip/pools/${poolId}/addresses/`,
        data,
      );
      await fetchAddresses();
      return response;
    },
    [api, poolId, fetchAddresses],
  );

  const updateAddress = useCallback(
    async (
      addressId: number,
      data: Partial<Omit<Address, 'id' | 'created_at'>>,
    ) => {
      const response = await api.patch<Address>(
        `/ip/pools/${poolId}/addresses/${addressId}/`,
        data,
      );
      await fetchAddresses();
      return response;
    },
    [api, poolId, fetchAddresses],
  );

  const deleteAddress = useCallback(
    async (addressId: number) => {
      await api.delete(`/ip/pools/${poolId}/addresses/${addressId}/`);
      await fetchAddresses();
    },
    [api, poolId, fetchAddresses],
  );

  const assignToDuck = useCallback(
    async (addressId: number, duckId: number) => {
      return updateAddress(addressId, { duck: duckId });
    },
    [updateAddress],
  );

  const release = useCallback(
    async (addressId: number) => {
      return updateAddress(addressId, { duck: null });
    },
    [updateAddress],
  );

  const setLeaseExpiry = useCallback(
    async (addressId: number, leaseExpires: string | null) => {
      return updateAddress(addressId, { lease_expires: leaseExpires });
    },
    [updateAddress],
  );

  const findAvailableAddress = useCallback(async () => {
    const allAddresses = await fetchAddresses(1, 1000);
    return allAddresses.results.find((addr) => !addr.duck);
  }, [fetchAddresses]);

  return {
    addresses,
    totalCount,
    nextPage,
    previousPage,
    loading: api.loading,
    error: api.error,
    fetchAddresses,
    getAddress,
    createAddress,
    updateAddress,
    deleteAddress,
    assignToDuck,
    release,
    setLeaseExpiry,
    findAvailableAddress,
  };
}
